import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { useToast } from "../components/ui/Toast";
import { useAdaptersApi } from '../apis/adapters-apis';
import { Adapter } from '../types';
import { Eye, Plus, Search, Trash2, ChevronUp, Loader2, Package } from 'lucide-react';
import _ from 'lodash';
import { format } from 'date-fns';

const ITEMS_PER_PAGE = 10;

export const AdaptersList: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const adaptersApi = useAdaptersApi();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(false);

  // State management
  const [adapters, setAdapters] = useState<Adapter[]>([]);
  const [displayedAdapters, setDisplayedAdapters] = useState<Adapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Adapter[] | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adapterToDelete, setAdapterToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  // Initial data fetch
  useEffect(() => {
    const controller = new AbortController();

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const fetchedAdapters = await adaptersApi.getAdapters();
        
        if (isMounted.current) {
          setAdapters(fetchedAdapters);
          setTotalCount(fetchedAdapters.length);
          setDisplayedAdapters(fetchedAdapters.slice(0, ITEMS_PER_PAGE));
          setHasMore(fetchedAdapters.length > ITEMS_PER_PAGE);
        }
      } catch (error) {
        if (isMounted.current && error instanceof Error && error.name !== 'AbortError') {
          // Do nothing
          addToast({
            title: "Error",
            description: "Failed to retrieve adapters. Please try again.",
            variant: "destructive",
          });
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    isMounted.current = true;
    fetchInitialData();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []);

  const loadMoreAdapters = () => {
    if (searchTerm || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const startIndex = displayedAdapters.length;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    
    if (startIndex >= adapters.length) {
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const newItems = adapters.slice(startIndex, endIndex);
    setDisplayedAdapters(prev => [...prev, ...newItems]);
    
    if (endIndex >= adapters.length) {
      setHasMore(false);
    }

    setLoadingMore(false);
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !searchTerm) {
        loadMoreAdapters();
      }
    }, options);

    const loader = document.querySelector('#scroll-loader');
    if (loader) {
      observer.observe(loader);
    }

    return () => observer.disconnect();
  }, [loadMoreAdapters, hasMore, loadingMore, searchTerm]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = adapters.filter(adapter =>
        adapter.adapterName.toLowerCase().includes(term.toLowerCase()) ||
        adapter.adapterDescription?.toLowerCase().includes(term.toLowerCase()) ||
        adapter.adapterTags?.some(tag => tag.toLowerCase().includes(term.toLowerCase()))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      setDisplayedAdapters(adapters.slice(0, ITEMS_PER_PAGE));
      setHasMore(adapters.length > ITEMS_PER_PAGE);
    }
  }, 300);

  const handleViewAdapter = (adapterId: string) => {
    navigate(`/studio/admin/adapters/edit/${adapterId}`);
  };

  const handleDeleteClick = (id: string) => {
    setAdapterToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!adapterToDelete) return;

    setIsDeletingId(adapterToDelete);
    try {
      await adaptersApi.deleteAdapter(adapterToDelete);
      addToast({
        title: "Success",
        description: "Adapter successfully deleted",
        variant: "default",
      });
      
      // Refresh the list
      const fetchedAdapters = await adaptersApi.getAdapters();
      setAdapters(fetchedAdapters);
      setTotalCount(fetchedAdapters.length);
      setDisplayedAdapters(fetchedAdapters.slice(0, ITEMS_PER_PAGE));
      setHasMore(fetchedAdapters.length > ITEMS_PER_PAGE);
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to delete adapter. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setAdapterToDelete(null);
    }
  };

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);
    }, 200);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentAdapters = searchResults || displayedAdapters;
  const currentTotal = searchResults ? searchResults.length : totalCount;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="pb-5 border-b border-gray-200 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Adapters</h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage your adapters and configurations
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md h-10"
                  placeholder="Search adapters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                  <Package className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Adapters</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {totalCount}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                All Adapters
              </h3>
              <Button
                onClick={() => navigate('/studio/admin/adapters/add')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="-ml-1 mr-2 h-5 w-5" />
                New Adapter
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center my-12">
              <Spinner className="h-8 w-8 text-indigo-500" />
              <p className="mt-2 text-sm text-gray-500">Loading adapters...</p>
            </div>
          ) : currentAdapters.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No adapters</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm
                  ? 'No adapters found matching your search.'
                  : 'Get started by creating a new adapter.'}
              </p>
              <div className="mt-6">
                <Button
                  onClick={() => navigate('/studio/admin/adapters/add')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Plus className="-ml-1 mr-2 h-5 w-5" />
                  New Adapter
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {currentAdapters.map((adapter) => (
                  <li key={adapter.adapterId} className="hover:bg-gray-50">
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-indigo-100">
                              <Package className="h-5 w-5 text-indigo-500" />
                            </div>
                            <div className="ml-4">
                              <div className="flex items-center">
                                <h2 className="text-lg font-medium text-indigo-600 truncate">
                                  {adapter.adapterName}
                                </h2>
                                {adapter.adapterTags && adapter.adapterTags.length > 0 && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                    {adapter.adapterTags[0]}
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                                {adapter.adapterDescription || 'No description provided'}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewAdapter(adapter.adapterId || '')}
                            className="text-indigo-600 bg-white hover:bg-indigo-50"
                          >
                            <Eye className="-ml-1 mr-2 h-4 w-4" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(adapter.adapterId || '');
                            }}
                            className="text-red-600 bg-white hover:bg-red-50"
                            disabled={isDeletingId === adapter.adapterId}
                          >
                            {isDeletingId === adapter.adapterId ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="-ml-1 mr-2 h-4 w-4" />
                            )}
                            {isDeletingId === adapter.adapterId ? 'Deleting...' : 'Delete'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!searchTerm && hasMore && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-center">
                <Button
                  onClick={loadMoreAdapters}
                  disabled={loadingMore}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Adapter"
        description="This will permanently delete the adapter from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-indigo-500 hover:bg-indigo-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default AdaptersList;